import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { PhEye } from 'ember-phosphor-icons';
import type PreviewService from 'tio-employee/services/preview';
import type SessionContextService from 'tio-employee/services/session-context';
import type IntlService from 'ember-intl/services/intl';

/** Names the preview and counts the grant down; once it expires, dims the page so nothing stale reads as live. */
export default class PreviewBanner extends Component {
  @service declare preview: PreviewService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;

  @tracked timeRemaining = '';

  /** Ember owns the interval's teardown, so a torn-down banner cannot tick on. */
  countdown = modifier(() => {
    this.#tick();
    const id = setInterval(() => this.#tick(), 1000);
    return () => clearInterval(id);
  });

  #tick() {
    const expiresAt = this.preview.expiresAt;
    if (!expiresAt) {
      this.timeRemaining = '';
      return;
    }
    const diff = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    this.timeRemaining = `${mins}:${String(secs).padStart(2, '0')}`;
  }

  get personName() {
    return this.sessionContext.person?.fullName || this.intl.t('preview.unnamed_person');
  }

  get surfaceName() {
    return this.intl.t(`preview.surface.${this.preview.surface ?? 'employee'}`);
  }

  <template>
    {{#if this.preview.isActive}}
      {{#if this.preview.expired}}
        {{! Above every page overlay (tio-ui modals and the mobile sidebar sit at z-50); the banner after it stays on top. }}
        <div
          class="fixed inset-0 z-9999 tio-bg-page opacity-70"
          aria-hidden="true"
          data-test-preview-expired-overlay
        ></div>
      {{/if}}
      <div
        class="sticky top-0
          {{if this.preview.expired 'z-9999' 'z-50'}}
          tio-bg-info-soft border-b tio-border-info px-4 py-2 tio-row tio-text-sm-normal tio-fg-info"
        data-test-preview-banner
        {{this.countdown}}
      >
        <PhEye @size={{18}} @weight="fill" class="tio-fg-info shrink-0" />

        {{! The viewed person's name is the PII here, and session replay masks on this class. }}
        <span class="flex-1 pii" role="status">
          {{#if this.preview.expired}}
            {{t "preview.expired_message"}}
          {{else}}
            {{t "preview.banner_message" personName=this.personName surface=this.surfaceName}}
          {{/if}}
        </span>

        {{#unless this.preview.expired}}
          {{#if this.timeRemaining}}
            {{! Outside the status region and aria-hidden: a per-second value would otherwise be announced per second. }}
            <span
              class="font-mono tio-text-xs-semibold tio-fg-info tio-bg-info-soft px-2 py-1 rounded"
              aria-hidden="true"
              data-test-preview-countdown
            >
              {{this.timeRemaining}}
            </span>
          {{/if}}
          <span class="tio-text-xs-semibold shrink-0" data-test-preview-read-only>
            {{t "preview.read_only_hint"}}
          </span>
        {{/unless}}
      </div>

    {{/if}}
  </template>
}
